import type { Route } from "next";
import Link from "next/link";
import type { ComponentProps, ReactNode } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { metaTextClassName, statValueClassName } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

const statToneClassNames = {
  default: "text-foreground",
  positive: "text-positive",
  negative: "text-negative",
  success: "text-success-foreground",
  warning: "text-warning-foreground",
  info: "text-info-foreground",
};

type StatTone = keyof typeof statToneClassNames;

type StatTileProps = {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  /** Money values use `positive`/`negative`; other counts use feedback tones. */
  tone?: StatTone;
  /** Makes the whole tile a link; the label is the accessible name. */
  href?: Route;
  className?: string;
};

/**
 * Label/value metric for dashboards and summaries. Render inside `StatGrid`,
 * which provides the `dl` element; tiles are separated by a left rule, not cards.
 */
export function StatTile({ label, value, hint, tone = "default", href, className }: StatTileProps) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-0.5 border-l pl-3",
        href && "group/stat hover:border-foreground relative transition-colors",
        className,
      )}
    >
      <dt className={cn(metaTextClassName, "truncate")}>
        {href ? (
          <Link
            className="group-hover/stat:text-foreground focus-visible:after:ring-ring/50 rounded-sm transition-colors outline-none after:absolute after:inset-0 focus-visible:after:ring-2"
            href={href}
          >
            {label}
          </Link>
        ) : (
          label
        )}
      </dt>
      <dd className={cn(statValueClassName, "truncate", statToneClassNames[tone])}>{value}</dd>
      {hint ? <dd className={cn(metaTextClassName, "truncate")}>{hint}</dd> : null}
    </div>
  );
}

/** Responsive grid for `StatTile`s. Override columns through `className`. */
export function StatGrid({ className, ...props }: ComponentProps<"dl">) {
  return <dl className={cn("grid grid-cols-2 gap-4 lg:grid-cols-4", className)} {...props} />;
}

/**
 * Loading value for `StatTile`. Sized in line-height units so the tile keeps the height
 * of a loaded value; pass a width that matches the expected value.
 */
export function StatValueSkeleton({ className }: { className?: string }) {
  return <Skeleton className={cn("my-[0.15lh] h-[0.7lh] w-12", className)} />;
}
